
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  section: 'Transfer',
  title: 'Glossary',
  description: 'Glossary of Transfer-specific terminology'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <p>{`This glossary contains terms specific to the Plaid Transfer product. Most terms in this glossary are industry terms related to funds transfers.`}</p>
    <p>{`For a general glossary of Plaid API terminology not related to the functionality of the Transfer product specifically, see the `}<a parentName="p" {...{
        "href": "/docs/quickstart/glossary/"
      }}>{`Plaid Glossary`}</a>{`.`}</p>
    <h4 {...{
      "id": "payment-rails"
    }}>{`Payment rails`}</h4>
    <h5 {...{
      "id": "ach"
    }}>{`ACH`}</h5>
    <p>{`ACH (Automated Clearing House) is the primary US electronic network for processing bank-to-bank payments, such as direct deposits, bill payments, and transfers, typically settled in batches. It enables low-cost, reliable fund transfers between accounts, often taking 1–3 business days. `}</p>
    <h5 {...{
      "id": "standard-ach"
    }}>{`Standard ACH`}</h5>
    <p>{`Standard ACH payments are payments sent over the ACH network without expedited processing. These typically complete within 1-3 business days. For transfers submitted via Plaid, the Standard ACH cutoff time is 8:30 PM Eastern Time. It is recommended to submit a request at least 15 minutes before the cutoff time in order to ensure that it will be processed before the cutoff.`}</p>
    <h5 {...{
      "id": "same-day-ach"
    }}>{`Same Day ACH`}</h5>
    <p>{`Same Day ACH uses the ACH network to allow eligible bank-to-bank transfers to be processed and settled on the same business day, typically within a few hours. For transfers submitted via Plaid as Same Day ACH, the Same Day ACH cutoff is 3:00 PM Eastern Time and the Standard ACH cutoff is 8:30 PM Eastern Time. It is recommended to submit a request at least 15 minutes before the cutoff time in order to ensure that it will be processed before the cutoff. If a Same Day ACH transfer is processed after the Same Day ACH cutoff but before the Standard ACH cutoff, it will be sent over Standard ACH rails and will not incur Same Day ACH charges; this will apply to both legs of the transfer if applicable.`}</p>
    <h5 {...{
      "id": "fednow"
    }}>{`FedNow`}</h5>
    <p>{`FedNow is a real-time payment service launched by the Federal Reserve that enables instant, 24/7/365 bank-to-bank transfers in the U.S., with immediate funds availability to recipients. Like RTP, FedNow only supports `}<inlineCode parentName="p">{`credit`}</inlineCode>{` transfers. FedNow payments are irreversible once sent. Within the Plaid Transfer API, payments designated as `}<inlineCode parentName="p">{`rtp`}</inlineCode>{` will be routed via either FedNow or RTP depending on availability. To check whether an institution supports real-time payments, use `}<inlineCode parentName="p">{`/transfer/capabilities/get`}</inlineCode>{`.`}</p>
    <h5 {...{
      "id": "rtp"
    }}>{`RTP`}</h5>
    <p>{`RTP, or Real-Time Payments, is a U.S. payment rail administered by The Clearing House. RTP payments provide immediate, 24/7/365 settlement of payments between participating banks, with funds available to the recipient within seconds. Like FedNow, RTP only supports `}<inlineCode parentName="p">{`credit`}</inlineCode>{` transfers. RTP payments are irreversible once sent. Within the Plaid Transfer API, payments designated as `}<inlineCode parentName="p">{`rtp`}</inlineCode>{` will be routed via either FedNow or RTP depending on availability. To check whether an institution supports real-time payments, use `}<inlineCode parentName="p">{`/transfer/capabilities/get`}</inlineCode>{`.`}</p>
    <h5 {...{
      "id": "rfp"
    }}>{`RfP`}</h5>
    <p>{`RfP, or Request for Payment, is a mechanism by which you can initiate the equivalent of a debit funds transfer using the credit-only real-time RTP or FedNow rails. When an RfP is sent to an end user, they will be prompted to approve the RfP, which will then initiate a credit transfer to you for the given amount. To check whether an institution supports RfP, use `}<inlineCode parentName="p">{`/transfer/capabilities/get`}</inlineCode>{`. Support for RfP within Plaid Transfer is currently in a closed limited beta. To express interest, contact your account manager.`}</p>
    <h5 {...{
      "id": "wire"
    }}>{`Wire`}</h5>
    <p>{`Wires, or wire transfers, are electronic funds transfers that move money directly between banks, typically within hours. Wires are only supported for credit transfers. Wires are irreversible once sent. The fee for wires is greater than for other payment methods. To request access to wires, contact your account manager. For more details on wires, see `}<a parentName="p" {...{
        "href": "/docs/transfer/creating-transfers/#initiating-a-payout-via-wire"
      }}>{`Initiating a payout via wire`}</a>{`.`}</p>
    <h4 {...{
      "id": "ach-terms"
    }}>{`ACH terms`}</h4>
    <h5 {...{
      "id": "nacha"
    }}>{`Nacha`}</h5>
    <p>{`Nacha is the trade association that manages the ACH network and establishes the rules and procedures around ACH, including what constitutes valid authorization for an ACH transaction and under what circumstances an ACH transaction may be returned or reversed. For more details on these topics, see `}<a parentName="p" {...{
        "href": "https://support.plaid.com/hc/en-us/articles/32881513531671-What-are-my-obligations-as-an-ACH-Originator"
      }}>{`What are my responsibilities as an ACH originator?`}</a>{`.`}</p>
    <h5 {...{
      "id": "odfi"
    }}>{`ODFI`}</h5>
    <p>{`The ODFI, or the Originating Depository Financial Institution, is the bank in an ACH transaction that initiates the transfer. The direction of funds movement is expressed from the perspective of the ODFI -- in the case of a debit transaction, the ODFI is the recipient of the funds, and in the case of a credit transaction, the ODFI is the sender. The ODFI's counterparty bank in the transaction is known as the RDFI, or the Receiving Depository Financial Institution. When you use Plaid Transfer to initiate a transaction, the ODFI is Plaid's Banking Partner.`}</p>
    <h5 {...{
      "id": "rdfi"
    }}>{`RDFI`}</h5>
    <p>{`The RDFI, or the Receiving Depository Financial Institution, is the bank in an ACH transaction that receives the transfer request from the ODFI. In the case of a debit transaction, the RDFI is the sender of the funds, and in the case of a credit transaction, the RDFI is the recipient. When using Plaid Transfer, the RDFI is typically your end user's bank where they linked their account. In the case of funds movement to or from your Plaid Ledger, initiated from a Plaid UI such as the Dashboard, Transfer API, or automatic balance transfers, the RDFI is your bank. (If moving money to your Ledger via an ACH transfer initiated from your bank, the RDFI is Plaid's Banking Partner and the ODFI is your bank.)`}</p>
    <h5 {...{
      "id": "recall"
    }}>{`Recall`}</h5>
    <p>{`A recall is a request submitted by the Originator to undo an ACH transfer that cannot be reversed or canceled. For more details, see `}<a parentName="p" {...{
        "href": "https://support.plaid.com/hc/en-us/articles/32881719199767-How-are-ACH-Transfer-Investigations-Escalated#h_01JXZT0SCV68P41TP5MSG2E6M3"
      }}>{`How do I request a recall?`}</a>{`.`}</p>
    <h5 {...{
      "id": "return"
    }}>{`Return`}</h5>
    <p>{`A return, or ACH Return, occurs when an ACH transaction is rejected or reversed by the receiving bank after it has been submitted, due to issues like insufficient funds, invalid account details, unauthorized debits, or other errors. Returns must be initiated within a specific timeframe (e.g., 2 banking days for most reasons, 60 days for unauthorized consumer debits). Each return comes with a reason code (e.g., R01 = NSF, R03 = No Account). For more details on dealing with returns, see `}<a parentName="p" {...{
        "href": "/docs/transfer/troubleshooting/#ach-returns"
      }}>{`ACH Returns`}</a>{`. Returns will not occur when using RTP, FedNow, or wire transfers, as these funds transfer methods are irreversible. In the rare instance that a wire is rejected by the receiving bank, the transfer will be returned; for more details, see `}<a parentName="p" {...{
        "href": "/docs/transfer/creating-transfers/#initiating-a-payout-via-wire"
      }}>{`Initiating a payout via wire`}</a>{`.`}</p>
    <h5 {...{
      "id": "reversal"
    }}>{`Reversal`}</h5>
    <p>{`An ACH reversal is a request submitted by the Originator to the ODFI to cancel or correct a previously submitted ACH transaction that has already been processed, typically due to an error such as duplicate payment, wrong amount, or incorrect account number. Reversals must be initiated within 5 banking days of the original settlement date. For details, see `}<a parentName="p" {...{
        "href": "https://support.plaid.com/hc/en-us/articles/32881719199767-How-are-ACH-Transfer-Investigations-Escalated#h_01JXZT18R8C24G31SB3C1P7PMR"
      }}>{`How do I request a reversal?`}</a>{`. Reversals are not allowed when using RTP, FedNow, or wire transfers, as these funds transfer methods are irreversible.`}</p>
    <h4 {...{
      "id": "payment-statuses"
    }}>{`Payment statuses`}</h4>
    <Callout mdxType="Callout">
      <p>{`For more information on payment statuses and their timelines, see the `}<a parentName="p" {...{
          "href": "/docs/transfer/reconciling-transfers#transfer-status-lifecycle"
        }}>{`Transfer status lifecycle`}</a>{`. `}</p>
    </Callout>
    <h5 {...{
      "id": "cancelled"
    }}>{`Cancelled`}</h5>
    <p>{`The payment was cancelled by the requestor while the payment was in a pending state. No money was moved. This is a terminal state. For more information, see `}<a parentName="p" {...{
        "href": "/docs/transfer/creating-transfers/#canceling-transfers"
      }}>{`Cancelling transfers`}</a>{`.`}</p>
    <h5 {...{
      "id": "failed"
    }}>{`Failed`}</h5>
    <p>{`A failed payment was rejected by either Plaid or the financial institution. No money was moved. This is a terminal state. For more information on handling failures, see `}<a parentName="p" {...{
        "href": "/docs/transfer/troubleshooting/"
      }}>{`Errors and troubleshooting`}</a>{`.`}</p>
    <h5 {...{
      "id": "funds-available"
    }}>{`Funds available`}</h5>
    <p>{`Funds from the transfer have been released from hold and applied to the Ledger's available balance. This state is only applicable to ACH debits. This is the terminal state of a successful debit transfer.`}</p>
    <h5 {...{
      "id": "pending"
    }}>{`Pending`}</h5>
    <p>{`A pending payment has not yet been submitted to the payment network and may still be canceled. For information on when payments are submitted to the network, see `}<a parentName="p" {...{
        "href": "/docs/transfer/creating-transfers/#ach-processing-windows"
      }}>{`ACH processing windows`}</a>{` and `}<a parentName="p" {...{
        "href": "/docs/transfer/creating-transfers/#initiating-a-payout-via-wire"
      }}>{`Initiating a payout via wire`}</a>{`.`}</p>
    <h5 {...{
      "id": "posted"
    }}>{`Posted`}</h5>
    <p>{`A posted payment has been submitted to the payment network but has not yet settled. For information on settlement timelines, see the `}<a parentName="p" {...{
        "href": "/docs/transfer/reconciling-transfers#transfer-status-lifecycle"
      }}>{`Transfer Status Lifecycle`}</a></p>
    <h5 {...{
      "id": "settled"
    }}>{`Settled`}</h5>
    <p>{`A payment is settled when funds have been transferred between the sending financial institution and the receiving financial institution. Settlement does not guarantee that the institution has released the funds to the recipient; a settled payment may still appear as a pending transaction in the recipient's or sender's bank account. For credit transfers, `}<inlineCode parentName="p">{`settled`}</inlineCode>{` is the terminal state of a successful payment. For debit transfers, the terminal state of a successful payment is `}<inlineCode parentName="p">{`funds_available`}</inlineCode>{`, which a settled debit reaches once your debit hold period has elapsed (typically 2-5 business days; see `}<a parentName="p" {...{
        "href": "/docs/transfer/dashboard/#limits-reserves-and-hold-time"
      }}>{`hold time`}</a>{` for details) and the funds are released from the Ledger's pending balance to its available balance. A debit can still transition to the `}<a parentName="p" {...{
        "href": "#returned"
      }}>{`returned`}</a>{` state after reaching `}<inlineCode parentName="p">{`funds_available`}</inlineCode>{`; most returns occur within 2 banking days of settlement, but consumer debits can be returned as unauthorized for up to 60 calendar days. For more details, see `}<a parentName="p" {...{
        "href": "/docs/transfer/troubleshooting/#ach-returns"
      }}>{`ACH returns`}</a>{` and the `}<a parentName="p" {...{
        "href": "/docs/transfer/reconciling-transfers#transfer-status-lifecycle"
      }}>{`Transfer Status Lifecycle`}</a>{`.`}</p>
    <h5 {...{
      "id": "returned"
    }}>{`Returned`}</h5>
    <p>{`A payment has experienced an `}<a parentName="p" {...{
        "href": "#return"
      }}>{`ACH return`}</a>{`. This is a terminal state. For more details on dealing with returned payments, see `}<a parentName="p" {...{
        "href": "/docs/transfer/troubleshooting/#ach-returns"
      }}>{`ACH Returns`}</a>{`.`}</p>
    <h4 {...{
      "id": "other-transfer-terms"
    }}>{`Other Transfer terms`}</h4>
    <h5 {...{
      "id": "fbo-account"
    }}>{`FBO account`}</h5>
    <p>{`Plaid's FBO ("for benefit of") account is used to hold money used in transfer operations. This is the bank account where you send money to fund your Ledger, and where money is held until it is disbursed to your account; it is the bank account where the money that backs your Ledger is actually stored. The FBO designation indicates that while Plaid is the custodian of the funds, the funds are held "for the benefit of" (i.e., belong to) Plaid's Transfer customers. `}</p>
    <h5 {...{
      "id": "funding-account"
    }}>{`Funding account`}</h5>
    <p>{`The business bank account belonging to your organization and linked to your Ledger. The funding account is used as the ultimate source of funds for credit transfers and the ultimate recipient of the funds for debit transfers. `}</p>
    <h5 {...{
      "id": "ledger"
    }}>{`Ledger`}</h5>
    <p>{`The Ledger, or the Plaid Ledger, is a balance you keep with Plaid for use with the Transfer product. It is a settlement balance that all debit transfers settle into and all credits are paid out from. For details on working with the Ledger, including moving money into and out of the Ledger or maintaining multiple Ledgers, see `}<a parentName="p" {...{
        "href": "/docs/transfer/flow-of-funds/"
      }}>{`Plaid Ledger flow of funds`}</a>{`.`}</p>
    <h5 {...{
      "id": "originator"
    }}>{`Originator`}</h5>
    <p>{`The Originator is the party who is requesting a funds transfer. When using Plaid Transfer, you are the originator of all transfers you request unless you are using Transfer for Platforms (beta). For more details on the distinction between Originators and Platforms, see `}<a parentName="p" {...{
        "href": "/docs/transfer/application/#originators-vs-platforms"
      }}>{`Originators vs Platforms`}</a>{`.`}</p>
    <h5 {...{
      "id": "sweep"
    }}>{`Sweep`}</h5>
    <p>{`A sweep is a transfer of money, typically automated or pre-programmed, between two accounts held by the same party to optimize the usage or availability of funds. For example, a brokerage account might automatically sweep money from a cash account to a money market account to earn higher interest while maintaining liquidity. In the context of Plaid Transfer, a sweep refers to any money movement between your Plaid Ledger(s) and your bank account. To learn more about sweeps, see `}<a parentName="p" {...{
        "href": "/docs/transfer/creating-transfers/#sweeping-funds-to-funding-accounts"
      }}>{`Sweeping funds to funding accounts`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "payment-rails",
        level: 4,
        title: "Payment rails",
      },
{
        id: "ach-terms",
        level: 4,
        title: "ACH terms",
      },
{
        id: "payment-statuses",
        level: 4,
        title: "Payment statuses",
      },
{
        id: "other-transfer-terms",
        level: 4,
        title: "Other Transfer terms",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
